import { Type, plainToInstance } from 'class-transformer'
import { FolderPath } from '@core/entities/folders/FolderResponse'
import { Entity } from '@core/entities/entity'
import { User } from '@core/entities/user'
import {
  ISiteBoxDocument,
  PermittedAction,
  StatusType,
} from '@core/entities/SiteBoxDocument'
import { localizedStrings } from '@core/strings'
import { SiteBoxDocumentPdfVersion } from '@core/entities/SiteBoxDocumentPdfVersion'
import { SiteBoxDocumentVersionBase } from '@core/entities/SiteBoxDocumentVersionBase'
import { SiteBoxDocumentNonPdfVersion } from '@core/entities/SiteBoxDocumentNonPdfVersion'
import { DocumentIntelligence } from './DocumentIntelligence'
import { FileFormat } from './types'
import { FILE_FORMATS } from './fileFormat'
import { statuses } from '@core/theme/statuses'

export class SiteBoxDocument extends Entity<ISiteBoxDocument> {
  id: number
  name: string

  @Type(() => User)
  modifiedBy: User

  modifiedOn: string

  @Type(() => User)
  createdBy: User

  createdOn: string
  folder: {
    folderId: number
    pathFromRoot: FolderPath
  }
  status: StatusType
  permittedActions: PermittedAction[]

  @Type(() => SiteBoxDocumentVersionBase, {
    discriminator: {
      property: 'format',
      subTypes: [
        { value: SiteBoxDocumentPdfVersion, name: 'PDF' },
        // Non-PDF formats are stored natively (no raster/annotation data) and
        // hydrate as the lean base version — `documentGetUrl` /
        // `documentDownloadUrl` are all a download/image/video preview needs.
        // Mapping them explicitly avoids relying on class-transformer's
        // version-dependent fallback for unmatched discriminator values.
        ...Object.keys(FILE_FORMATS)
          .filter((format) => format !== 'PDF')
          .map((format) => ({
            value: SiteBoxDocumentNonPdfVersion,
            name: format,
          })),
      ],
    },
    keepDiscriminatorProperty: true,
  })
  // Narrow with `instanceof SiteBoxDocumentPdfVersion` before reading PDF-only
  // fields/methods (register diagrams etc.).
  currentVersion?: SiteBoxDocumentPdfVersion | SiteBoxDocumentNonPdfVersion

  totalVersions?: number

  @Type(() => DocumentIntelligence)
  documentIntelligence: DocumentIntelligence

  public static new(payload: ISiteBoxDocument) {
    const entity = plainToInstance(SiteBoxDocument, payload)

    return entity
  }

  public getStatusColor() {
    switch (this.status) {
      case 'LIVE':
        return statuses.active.color
      case 'ARCHIVED':
        return statuses.archived.color
      default:
        return 'default'
    }
  }

  public getStatusText() {
    switch (this.status) {
      case 'LIVE':
        return statuses.active.label
      case 'ARCHIVED':
        return statuses.archived.label
      default:
        return this.status
    }
  }

  public isLive() {
    return this.status === 'LIVE'
  }

  public isArchived() {
    return this.status === 'ARCHIVED'
  }

  public getDocumentIntelligenceStatusColor() {
    switch (this.documentIntelligence.status) {
      case 'PASS':
        return 'success'
      case 'FAIL':
        return 'error'
      case 'IN_REVIEW':
      case 'PROCESSING':
        return 'warning'
      case 'PROCESSED':
        return 'success'
      case 'NOT_PROCESSED':
        return 'default'
      default:
        return 'default'
    }
  }

  public getDocumentIntelligenceStatusText() {
    switch (this.documentIntelligence.status) {
      case 'PROCESSING':
        return statuses.processing.label
      case 'PROCESSED':
        return localizedStrings.complete
      case 'NOT_PROCESSED':
        return localizedStrings.notProcessed
      case 'PASS':
        return localizedStrings.passed
      case 'FAIL':
        return localizedStrings.failed
      case 'IN_REVIEW':
        return localizedStrings.inReview
      default:
        return ''
    }
  }

  public canBypassReview(): boolean {
    return this.documentIntelligence.config?.allowBypass
  }

  public getDocumentIntelligenceIcon():
    | 'PROCESSING'
    | 'NO_RESULTS'
    | 'PARTIALLY_FOUND'
    | 'NOT_FOUND'
    | 'FOUND' {
    if (this.documentIntelligence.status === 'PROCESSING') {
      return 'PROCESSING'
    }

    const results = this.documentIntelligence.result

    if (results.length === 0) {
      return 'NO_RESULTS'
    }

    const partiallyFoundCount = results.filter(
      (result) => result.result?.status === 'PARTIALLY_FOUND',
    ).length

    const notFoundCount = results.filter(
      (result) => result.result?.status === 'NOT_FOUND',
    ).length

    if (partiallyFoundCount > 0 && notFoundCount === 0) {
      return 'PARTIALLY_FOUND'
    } else if (notFoundCount > 0) {
      return 'NOT_FOUND'
    }

    if (
      results.length > 0 &&
      partiallyFoundCount === 0 &&
      notFoundCount === 0
    ) {
      return 'FOUND'
    }

    return 'NO_RESULTS'
  }
  public isDocumentAppraised(): boolean {
    return (
      this.documentIntelligence.status === 'PASS' ||
      this.documentIntelligence.status === 'FAIL'
    )
  }

  public canUserBypassReview(): boolean {
    return this.permittedActions?.includes('BYPASS')
  }

  public canUserAppraiseDocument(): boolean {
    return this.permittedActions?.includes('APPRAISE')
  }

  public getFileFormat(): FileFormat {
    return this.currentVersion?.format
  }

  public isImageFile(): boolean {
    return [FILE_FORMATS.JPEG.format, FILE_FORMATS.PNG.format].includes(
      this.getFileFormat(),
    )
  }
}
